import React, { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import Icon from 'react-native-vector-icons/Feather';
import { GlobalStyles, Spacing, Typography } from '../theme';
import { useI18n } from '../i18n';
import { useTheme } from '../theme/useTheme';
import { daylightStyle } from '../theme/daylightStyle';
import type { TonyteTheme } from '../theme/themes';
import {
  dateToString,
  isDateAfter,
  isDateBefore,
  parseToDate,
  timeMinutesBefore,
  type PickerFieldProps,
} from '../utils/pickerFieldDate';

// Web counterpart to PickerField.tsx. The native file only ever renders its
// real picker behind `Platform.OS === 'android' | 'ios'` checks — on web
// nothing rendered at all (confirmed live: tapping the field did nothing),
// which was a hard blocker for Create Event/Create Plan. This uses the
// browser's own `<input type="date">`/`<input type="time">`, which already
// speaks the exact same `YYYY-MM-DD`/`HH:mm` string format PickerField's
// `value`/`onChange` contract uses — no conversion needed, so
// UploadEventScreen.tsx/UploadPlanScreen.tsx need zero changes.
//
// The outer field box matches Tonyte's visual style (same border/background/
// radius/icon as native); the calendar/time popup itself is the browser's
// own native control, which cannot be restyled without a full custom picker
// widget — an explicit, reported scope tradeoff, not an oversight.
const PickerField: React.FC<PickerFieldProps> = ({
  value,
  onChange,
  onReject,
  placeholder,
  mode,
  minimumDate,
  maximumDate,
  noMargin,
}) => {
  const { t } = useI18n();
  const { theme } = useTheme();
  const appearance = useMemo(() => createThemeStyles(theme), [theme]);

  const min = mode === 'date' && minimumDate ? dateToString(minimumDate, 'date') : undefined;
  const max = mode === 'date' && maximumDate ? dateToString(maximumDate, 'date') : undefined;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    if (!next) return;
    const parsed = parseToDate(next, mode);
    if (mode === 'date' && minimumDate && isDateBefore(parsed, minimumDate)) {
      onReject?.();
      return;
    }
    if (mode === 'date' && maximumDate && isDateAfter(parsed, maximumDate)) {
      onReject?.();
      return;
    }
    if (mode === 'time' && minimumDate && timeMinutesBefore(parsed, minimumDate)) {
      onReject?.();
      return;
    }
    onChange(next);
  };

  return (
    <View style={[styles.field, !noMargin && styles.fieldWrap]}>
      <View style={[GlobalStyles.inputBase, styles.inputWrap, appearance.input]}>
        <input
          type={mode === 'date' ? 'date' : 'time'}
          value={value}
          min={min}
          max={max}
          onChange={handleChange}
          placeholder={placeholder}
          aria-label={placeholder}
          style={{
            flex: 1,
            border: 'none',
            outline: 'none',
            background: 'transparent',
            color: value ? theme.colors.textPrimary : theme.colors.textMuted,
            fontSize: Typography.base,
            fontFamily: 'inherit',
            colorScheme: theme.isDark ? 'dark' : 'light',
            minWidth: 0,
          }}
        />
        <Icon
          name={mode === 'date' ? 'calendar' : 'clock'}
          size={16}
          color={value ? theme.colors.iconSubtle : theme.colors.iconFaint}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  field: { flex: 1 },
  fieldWrap: { marginTop: Spacing.xs },
  inputWrap: { alignItems: 'center', flexDirection: 'row', justifyContent: 'space-between', gap: Spacing.sm },
});

const createThemeStyles = (theme: TonyteTheme) => StyleSheet.create({
  input: daylightStyle(theme, {
    backgroundColor: theme.colors.inputBackground,
    borderColor: theme.colors.borderStrong,
    color: theme.colors.textPrimary,
    ...theme.shadows.card,
  }),
});

export default PickerField;
